import { Component, OnInit, HostListener, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormControl,
} from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { PageHeaderComponent } from '../../../shared/components/layout/page-header/page-header.component';
import { TableComponent } from '../../../shared/components/ui/table/table.component';
import { SearchBarComponent } from '../../../shared/components/ui/search-bar/search-bar.component';
import { SortDropdownComponent } from '../../../shared/components/ui/sort-dropdown/sort-dropdown.component';
import { PaginationComponent } from '../../../shared/components/ui/pagination/pagination.component';
import { TableColumn } from '../../../shared/models/table.model';
import { ButtonModule } from 'primeng/button';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SelectModule } from 'primeng/select';
import { UserProfile, UserRole } from '../../../shared/models/auth.model';
import {
  UserData,
  UsersPaginationInfo,
} from '../../../shared/models/users.model';
import { AuthService } from '../../../core/services/auth.service';
import { UsersService } from '../../../shared/services/users.service';
import { CardListComponent } from '../../../shared/components/ui/card-list/card-list.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { formatDateDDMMYYYY } from '../../../shared/utils/date.util';
import { LoaderComponent } from '../../../shared/components/ui/loader/loader.component';

// Local interfaces for this component
interface FilterTab {
  id: string;
  label: string;
  count?: number;
}

interface SortOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-users-list',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,

    TableComponent,
    SearchBarComponent,

    SortDropdownComponent,
    PaginationComponent,
    ButtonModule,
    ToggleSwitchModule,
    SelectModule,
    CardListComponent,
    ModalComponent,
    LoaderComponent,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './users-list.component.html',
  styleUrl: './users-list.component.scss',
})
export class UsersListComponent implements OnInit, OnDestroy {
  // User profile for header
  userProfile: UserProfile | null = null;

  // Reactive form for filters and search
  filterForm: FormGroup;

  // Debounce subject for search
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // Getter for sort control to ensure proper typing
  get sortControl(): FormControl {
    return this.filterForm.get('selectedSortOption') as FormControl;
  }

  // Filter options
  typeOptions: { label: string; value: string }[] = [
    { label: 'All Types', value: 'all' },
    { label: 'Admin', value: 'admin' },
    { label: 'Manager', value: 'manager' },
    { label: 'Secretary', value: 'secretary' },
  ];

  statusOptions: { label: string; value: string }[] = [
    { label: 'All Status', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Archive', value: 'archive' },
  ];

  // Sort options
  sortOptions: SortOption[] = [
    { label: 'Newest First', value: 'newest' },
    { label: 'Oldest First', value: 'oldest' },
    { label: 'A-Z', value: 'nameasc' },
    { label: 'Z-A', value: 'namedesc' },
  ];

  // Pagination
  paginationInfo: UsersPaginationInfo = {
    currentPage: 1,
    totalPages: 34,
    totalItems: 339,
    itemsPerPage: 10,
    startItem: 1,
    endItem: 10,
  };

  // Table columns for users
  columns: TableColumn[] = [
    { key: 'fullName', label: 'Name', sortable: true },
    { key: 'emailAddress', label: 'Email Address', sortable: true },
    { key: 'createdOn', label: 'Date', sortable: true },
    { key: 'userRole', label: 'Type', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
  ];

  // Table data
  users: UserData[] = [];
  loading: boolean = false;

  // Archive functionality
  showDeleteModal: boolean = false;
  userToDelete: UserData | null = null;
  isDeleting: boolean = false;

  // Data will be loaded from the service

  constructor(
    private router: Router,
    private authService: AuthService,
    private usersService: UsersService,
    private fb: FormBuilder,
    private messageService: MessageService
  ) {
    this.filterForm = this.fb.group({
      selectedType: ['all'],
      selectedStatus: ['all'],
      selectedSortOption: ['newest'],
      searchTerm: [''],
    });
  }

  ngOnInit(): void {
    this.loadUsersData();
    this.checkUserRole();
    this.setupSearchDebounce();
    this.setupFormValueChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearchDebounce(): void {
    this.searchSubject
      .pipe(
        debounceTime(1000), // 1000ms delay - 1 second for user to finish typing
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((searchTerm: string) => {
        this.filterForm.patchValue({ searchTerm });
        this.paginationInfo.currentPage = 1;
        this.loadUsersData();
      });
  }

  private setupFormValueChanges(): void {
    // Note: Form value change listeners removed to prevent duplicate API calls
    // Event handlers now call loadUsersData() directly for immediate response
  }

  private checkUserRole(): void {
    const currentUser = this.authService.getCurrentUser();

    // Set userProfile from authenticated user
    if (currentUser) {
      this.userProfile = {
        id: currentUser.email,
        name: currentUser.name,
        email: currentUser.email,
        role: currentUser.role,
        avatar: 'assets/images/logo.png',
      };
    }
  }

  private loadUsersData(): void {
    this.loading = true;
    // Clear users array immediately to prevent showing stale data
    this.users = [];
    const formValue = this.filterForm.value;

    // Build filters object
    const filters: any = {
      Page: this.paginationInfo.currentPage,
      PageSize: this.paginationInfo.itemsPerPage,
    };

    // Handle Role filter - send the role name directly to API
    if (formValue.selectedType && formValue.selectedType !== 'all') {
      // Send the role name as expected by the API
      filters.Role = formValue.selectedType;
    }

    // Handle Search filter
    if (formValue.searchTerm && formValue.searchTerm.trim()) {
      filters.Search = formValue.searchTerm.trim();
    }

    // Handle SortBy filter - backend expects: newest, oldest, nameasc, namedesc
    if (formValue.selectedSortOption) {
      // Map frontend values to backend SortBy values
      const sortMapping: { [key: string]: string } = {
        'newest': 'newest',
        'oldest': 'oldest',
        'nameasc': 'nameasc',
        'namedesc': 'namedesc'
      };
      filters.SortBy = sortMapping[formValue.selectedSortOption] || formValue.selectedSortOption;
    } else {
      // Default to newest if no sort option is selected
      filters.SortBy = 'newest';
    }

    // Handle Status filter
    if (formValue.selectedStatus === 'archive') {
      filters.IsDeleted = true;
    } else if (formValue.selectedStatus && formValue.selectedStatus !== 'all') {
      // Map status values to API expected format
      const statusMapping: { [key: string]: string } = {
        'active': 'Active',
        'inactive': 'Inactive'
      };
      filters.Status = statusMapping[formValue.selectedStatus] || formValue.selectedStatus;
    }

    this.usersService.getUsersWithFilters(filters).subscribe({
      next: (response) => {
        // Format dates before assigning to users array
        this.users = response.data.map((user) => ({
          ...user,
          createdOn: formatDateDDMMYYYY(user.createdOn),
        }));
        this.paginationInfo.totalItems = response.total;
        this.paginationInfo.totalPages = Math.ceil(
          response.total / response.perPage
        );
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  // Filtering and sorting is now handled by the service

  /**
   * Parse date string in DD-MM-YYYY format to Date object
   */
  private parseDate(dateString: string): Date {
    const [day, month, year] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
  }

  // Event handlers for search and sort components

  onSearchChange(searchTerm: string) {
    this.searchSubject.next(searchTerm);
  }

  onTypeChange(type: string) {
    // Clear users array immediately to prevent showing stale data
    this.users = [];
    this.loading = true;
    this.paginationInfo.currentPage = 1;
    this.filterForm.patchValue({ selectedType: type });
    // Call loadUsersData immediately to avoid delay from form value changes
    this.loadUsersData();
  }

  onStatusChange(status: string) {
    // Clear users array immediately to prevent showing stale data
    this.users = [];
    this.loading = true;
    this.paginationInfo.currentPage = 1;
    this.filterForm.patchValue({ selectedStatus: status });
    // Call loadUsersData immediately to avoid delay from form value changes
    this.loadUsersData();
  }

  onSortChange(sortOption: string) {
    // Clear users array immediately to prevent showing stale data
    this.users = [];
    this.loading = true;
    this.paginationInfo.currentPage = 1;
    this.filterForm.patchValue({ selectedSortOption: sortOption });
    // Call loadUsersData immediately to avoid delay from form value changes
    this.loadUsersData();
  }

  onPageChange(page: number) {
    this.paginationInfo.currentPage = page;
    this.loadUsersData();
  }


  onEditUser(row: UserData) {
    // Check if we're viewing archived users - disable edit for archived users
    const formValue = this.filterForm.value;
    if (formValue.selectedStatus === 'archive') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Action Not Allowed',
        detail: 'Cannot edit archived users.',
        life: 3000,
      });
      return;
    }

    this.router.navigate(['/users/edit'], {
      queryParams: {
        userId: row.id,
      },
    });
  }

  onDeleteUser(row: UserData) {
    // Check if we're viewing archived users - disable delete for archived users
    const formValue = this.filterForm.value;
    if (formValue.selectedStatus === 'archive') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Action Not Allowed',
        detail: 'Cannot delete already archived users.',
        life: 3000,
      });
      return;
    }

    // Check if user is trying to delete an Admin user
    if (row.userRole?.toLowerCase() === 'admin') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Action Not Allowed',
        detail: 'Admin users cannot be deleted.',
        life: 5000,
      });
      return;
    }

    // Show confirmation modal
    this.userToDelete = row;
    this.showDeleteModal = true;
  }

  confirmDeleteUser() {
    // Double check that userToDelete exists and has an id
    if (!this.userToDelete || !this.userToDelete.id) {
      this.cancelDeleteUser();
      return;
    }

    // Prevent multiple archiving attempts
    if (this.isDeleting) {
      return;
    }

    // Store the archived user ID for reference BEFORE any async operations
    const deletedUserId = this.userToDelete.id;
    const deletedUserName = this.userToDelete.fullName;

    // Set deleting state to show loading feedback
    this.isDeleting = true;

    this.usersService.deleteUser(deletedUserId).subscribe({
      next: () => {
        // Show success toast
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'User deleted successfully.',
          life: 4000,
        });

        // Reset delete modal state first
        this.userToDelete = null;
        this.showDeleteModal = false;
        this.isDeleting = false;

        // Reload data to ensure consistency with server
        this.loadUsersData();

        // Alternative approach: Update local data if you prefer not to reload
        // this.updateLocalDataAfterArchive(deletedUserId);
      },
      error: () => {
        // Show error toast
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete user. Please try again.',
          life: 5000,
        });

        // Reset delete modal state
        this.userToDelete = null;
        this.showDeleteModal = false;
        this.isDeleting = false;
      },
    });
  }

  // Alternative method for local data update (if you prefer not to reload from server)
  private updateLocalDataAfterArchive(deletedUserId: string): void {
    // Remove user from local array
    const originalLength = this.users.length;
    this.users = this.users.filter((user) => user.id !== deletedUserId);

    // Only update pagination if user was actually removed
    if (this.users.length < originalLength) {
      // Update pagination info
      this.paginationInfo.totalItems--;
      this.paginationInfo.totalPages = Math.ceil(
        this.paginationInfo.totalItems / this.paginationInfo.itemsPerPage
      );

      // Update end item count
      this.paginationInfo.endItem = Math.min(
        this.paginationInfo.startItem + this.users.length - 1,
        this.paginationInfo.totalItems
      );

      // If current page is empty and not the first page, go to previous page
      if (this.users.length === 0 && this.paginationInfo.currentPage > 1) {
        this.paginationInfo.currentPage--;
        this.loadUsersData();
      }
    }
  }

  cancelDeleteUser() {
    this.userToDelete = null;
    this.showDeleteModal = false;
    this.isDeleting = false; // Reset deleting state as well
  }

  // Handle toggle switch changes
  onToggleStatus(user: UserData, event: any): void {
    // Check if we're viewing archived users - disable toggle for archived users
    const formValue = this.filterForm.value;
    if (formValue.selectedStatus === 'archive') {
      // Revert the toggle since archived users cannot be toggled
      event.checked = !event.checked;
      this.messageService.add({
        severity: 'warn',
        summary: 'Action Not Allowed',
        detail: 'Cannot change status of archived users.',
        life: 3000,
      });
      return;
    }

    const newStatus = event.checked ? 'Active' : 'Inactive';
    const isActive = event.checked;

    // Call API to update user status
    this.usersService
      .editUser(user.id, {
        fullName: user.fullName,
        roleName: user.userRole,
        isActive: isActive,
      })
      .subscribe({
        next: (response) => {
          // Update the user status in the local array
          user.status = newStatus;
          const userIndex = this.users.findIndex((u) => u.id === user.id);
          if (userIndex !== -1) {
            this.users[userIndex].status = newStatus;
          }

          // Show success toast
          this.messageService.add({
            severity: 'success',
            summary: 'Status Updated',
            detail:
              newStatus === 'Active'
                ? `User ${user.fullName} has been activated.`
                : `User ${user.fullName} has been deactivated.`,
            life: 3000,
          });
        },
        error: () => {
          // Show error toast
          this.messageService.add({
            severity: 'error',
            summary: 'Update Failed',
            detail: 'Failed to update user status. Please try again.',
            life: 4000,
          });

          // Revert the toggle if API call fails
          event.checked = !event.checked;
        },
      });
  }

  // Mobile dropdown toggles
  showTypeMenu: boolean = false;
  showStatusMenu: boolean = false;
  showSortMenu: boolean = false;

  toggleTypeMenu(): void {
    this.showTypeMenu = !this.showTypeMenu;
    if (this.showTypeMenu) {
      this.showStatusMenu = false;
      this.showSortMenu = false;
    }
  }

  toggleStatusMenu(): void {
    this.showStatusMenu = !this.showStatusMenu;
    if (this.showStatusMenu) {
      this.showTypeMenu = false;
      this.showSortMenu = false;
    }
  }

  toggleSortMenu(): void {
    this.showSortMenu = !this.showSortMenu;
    if (this.showSortMenu) {
      this.showTypeMenu = false;
      this.showStatusMenu = false;
    }
  }

  closeMenus(): void {
    this.showTypeMenu = false;
    this.showStatusMenu = false;
    this.showSortMenu = false;
  }
}
